/**
 * Enhanced Error Boundary Component
 * 
 * Provides better error recovery options and user experience.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Home, Copy, ChevronDown, ChevronUp } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { debug } from '../../lib/utils/debug'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  /**
   * Whether to show error details (default: development mode only)
   */
  showDetails?: boolean
  /**
   * Recovery actions to display
   */
  recoveryActions?: Array<{
    label: string
    action: () => void
  }>
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  showDetails: boolean
}

export class EnhancedErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorInfo: null
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    debug.error('EnhancedErrorBoundary caught an error:', error, errorInfo)
    
    this.setState({
      error,
      errorInfo
    })

    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    // In production, send to error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry.captureException(error, { contexts: { react: errorInfo } })
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleCopyError = () => {
    const errorText = `
Error: ${this.state.error?.message}
Stack: ${this.state.error?.stack}
Component Stack: ${this.state.errorInfo?.componentStack}
    `.trim()
    
    navigator.clipboard.writeText(errorText).then(() => {
      // Show toast notification
      const event = new CustomEvent('toast', {
        detail: { message: 'Error details copied to clipboard', type: 'success' }
      })
      window.dispatchEvent(event)
    })
  }

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }))
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const isDevelopment = process.env.NODE_ENV === 'development'
      const showDetails = this.props.showDetails ?? isDevelopment

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          showDetails={showDetails}
          detailsOpen={this.state.showDetails}
          onToggleDetails={this.toggleDetails}
          onReset={this.handleReset}
          onReload={this.handleReload}
          onCopyError={this.handleCopyError}
          recoveryActions={this.props.recoveryActions}
        />
      )
    }

    return this.props.children
  }
}

interface ErrorFallbackProps {
  error: Error | null
  errorInfo: ErrorInfo | null
  showDetails: boolean
  detailsOpen: boolean
  onToggleDetails: () => void
  onReset: () => void
  onReload: () => void
  onCopyError: () => void
  recoveryActions?: Array<{
    label: string
    action: () => void
  }>
}

function ErrorFallback({
  error,
  errorInfo,
  showDetails,
  detailsOpen,
  onToggleDetails,
  onReset,
  onReload,
  onCopyError,
  recoveryActions
}: ErrorFallbackProps) {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2">
          Something went wrong
        </h1>

        {/* Message */}
        <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
          We encountered an unexpected error. Don't worry, your data is safe.
        </p>

        {/* Error Message (if available) */}
        {error?.message && (
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              Error:
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {error.message}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <button
            onClick={onReset}
            className="flex-1 px-6 py-3 bg-salt-navy hover:bg-salt-navy-dark text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Try Again
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Home className="w-5 h-5" />
            Go Home
          </button>
          
          <button
            onClick={onReload}
            className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
          >
            Reload Page
          </button>
        </div>

        {/* Recovery Actions */}
        {recoveryActions && recoveryActions.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Additional Options:
            </p>
            <div className="space-y-2">
              {recoveryActions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.action}
                  className="w-full px-4 py-2 text-left bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm"
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error Details (Development/When enabled) */}
        {showDetails && error && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <button
              onClick={onToggleDetails}
              className="w-full flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors mb-4"
            >
              <span>Error Details</span>
              {detailsOpen ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>

            {detailsOpen && (
              <div className="space-y-4">
                {/* Stack Trace */}
                {error.stack && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Stack Trace
                      </p>
                      <button
                        onClick={onCopyError}
                        className="text-xs text-salt-navy hover:text-salt-navy-dark flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" />
                        Copy
                      </button>
                    </div>
                    <pre className="text-xs bg-gray-900 dark:bg-black text-green-400 p-4 rounded-lg overflow-x-auto font-mono">
                      {error.stack}
                    </pre>
                  </div>
                )}

                {/* Component Stack */}
                {errorInfo?.componentStack && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                      Component Stack
                    </p>
                    <pre className="text-xs bg-gray-900 dark:bg-black text-green-400 p-4 rounded-lg overflow-x-auto font-mono">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default EnhancedErrorBoundary

